import { apiFetch, ApiError } from '@/lib/api';

// Backend grant endpoint. Verifies the Jest-signed receipt (either a single
// `purchaseSigned` or a bulk `purchasesSigned`) with the Jest shared secret
// and credits coins idempotently.
const VERIFY_PATH = '/api/jest/payments/verify';

interface VerifyResponse {
  credited: boolean;
  coins: number;
}

function authHeaders(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}` };
}

async function grantSingle(purchaseSigned: string, accessToken: string): Promise<VerifyResponse> {
  return apiFetch<VerifyResponse>(VERIFY_PATH, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ purchaseSigned }),
  });
}

async function grantBatch(purchasesSigned: string, accessToken: string): Promise<VerifyResponse> {
  return apiFetch<VerifyResponse>(VERIFY_PATH, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ purchasesSigned }),
  });
}

// Drains every incomplete purchase the SDK is holding, grants each one on
// the backend, then tells Jest the purchase is complete. Safe to call on
// every boot — the backend grant is idempotent.
export async function recoverIncompletePurchases(
  sdk: JestSDKApi,
  accessToken: string,
): Promise<number> {
  let recovered = 0;
  let result: JestSDKIncompletePurchasesResult;

  do {
    result = await sdk.payments.getIncompletePurchases();
    if (result.purchases.length === 0) break;

    // Grant the whole batch first. If this throws, we bail without calling
    // completePurchase so Jest will surface the same purchases on next boot.
    await grantBatch(result.purchasesSigned, accessToken);

    for (const purchase of result.purchases) {
      await sdk.payments.completePurchase({ purchaseToken: purchase.purchaseToken });
      recovered += 1;
    }
  } while (result.hasMore);

  return recovered;
}

export interface PurchaseBundleResult {
  coinsAdded: number;
  purchase: JestSDKPurchaseData;
}

// Thrown when the player dismisses the Jest checkout modal. Callers should
// treat this as a no-op (no "Purchase Failed" toast), not as a real error.
export class PurchaseCancelledError extends Error {
  constructor() {
    super('Purchase cancelled by player');
    this.name = 'PurchaseCancelledError';
  }
}

// Buys a single product via Jest's hosted checkout, grants the coins on
// the backend, then marks the purchase complete.
//
// beginPurchase resolves to one of three result shapes; only the success
// branch carries a signed receipt. Cancel/error branches must NOT be sent to
// the backend — doing so would POST an empty body (purchaseSigned undefined →
// `{}` after JSON.stringify) which the backend rejects with INVALID_REQUEST.
export async function purchaseBundle(
  sdk: JestSDKApi,
  productSku: string,
  accessToken: string,
): Promise<PurchaseBundleResult> {
  const beginResult = await sdk.payments.beginPurchase({ productSku });

  if (beginResult.result === 'cancel') {
    throw new PurchaseCancelledError();
  }
  if (beginResult.result === 'error') {
    throw new Error(beginResult.error || 'Jest checkout failed');
  }

  const { purchase, purchaseSigned } = beginResult;

  let coinsAdded = 0;
  try {
    const grant = await grantSingle(purchaseSigned, accessToken);
    coinsAdded = grant.coins;
  } catch (err) {
    // Leave the purchase un-completed so the next boot recovers it.
    if (err instanceof ApiError) throw err;
    throw new Error('Failed to credit purchase');
  }

  await sdk.payments.completePurchase({ purchaseToken: purchase.purchaseToken });
  return { coinsAdded, purchase };
}
